import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Heading, LoadingSpinner, Text } from '../../components/ui'
import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  TagIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline'
import {
  Cell,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { comercialContatosPortalDashboardService } from '../../services/comercialContatosPortalDashboard'
import type { RelatorioDashboardMetricas, RelatorioPeriodo } from '../../types/comercialContatosPortal'
import {
  ContatosPortalCorretorFiltroInput,
  ContatosPortalFiltrosSection,
  ContatosPortalPeriodoFiltroInput,
  ContatosPortalResponsavelFiltroInput,
  FiltroField,
} from './ContatosPortalFiltros'
import { useContatosPortalFiltros } from './useContatosPortalFiltros'

/** Paleta alinhada ao superapp dashboard (azul, verde, vermelho, amarelo). */
const CHART_BLUE = '#4285F4'
const CHART_GREEN = '#34A853'
const CHART_RED = '#EA4335'
const CHART_YELLOW = '#FBBC05'

const PRIMARY_CHART = CHART_BLUE
const CHART_PALETTE = [CHART_BLUE, CHART_GREEN, CHART_RED, CHART_YELLOW]

const MEIO_COLORS: Record<string, string> = {
  telefone: CHART_BLUE,
  email: CHART_YELLOW,
  whatsapp: CHART_GREEN,
}

const TIPO_COLORS: Record<string, string> = {
  ativo: CHART_GREEN,
  receptivo: CHART_BLUE,
}

const PIE_FALLBACK_COLORS = CHART_PALETTE

function pieLabelWithColor(getColor: (index: number) => string) {
  return (props: {
    cx?: number
    cy?: number
    midAngle?: number
    innerRadius?: number
    outerRadius?: number
    percent?: number
    index?: number
    name?: string
    value?: number
  }) => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      innerRadius = 0,
      outerRadius = 0,
      percent = 0,
      index = 0,
      name = '',
      value = 0,
    } = props
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 1.12
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)
    const fill = getColor(index)
    const pct = (percent * 100).toFixed(0)
    return (
      <text
        x={x}
        y={y}
        fill={fill}
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={500}
      >
        {`${name}: ${value} (${pct}%)`}
      </text>
    )
  }
}

const tooltipStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: '0.5rem',
}

type MetricCardDef = {
  label: string
  value: string | number
  gradientClass: string
  subtitleClass?: string
}

function GradientKpiCard({ label, value, gradientClass, subtitleClass = 'text-white/90' }: MetricCardDef) {
  return (
    <div className={`bg-gradient-to-br shadow-lg rounded-lg p-6 text-white ${gradientClass}`}>
      <div className="text-center">
        <Text size="3xl" className="text-white font-bold mb-2">
          {value}
        </Text>
        <Text size="sm" className={subtitleClass}>
          {label}
        </Text>
      </div>
    </div>
  )
}

type RankedItem = {
  key: string
  title: string
  subtitle: string
}

function RankedList({ items, emptyMessage }: { items: RankedItem[]; emptyMessage: string }) {
  if (items.length === 0) {
    return <Text color="muted">{emptyMessage}</Text>
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => (
        <div
          key={item.key}
          className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Text
              size="sm"
              weight="bold"
              className="text-primary-600 dark:text-primary-400 tabular-nums w-6 shrink-0"
            >
              {index + 1}
            </Text>
            <div className="min-w-0">
              <Text weight="semibold" className="truncate" title={item.title}>
                {item.title}
              </Text>
              <Text size="sm" color="muted">
                {item.subtitle}
              </Text>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

const ContatosPortalDashboardTab: React.FC = () => {
  const {
    loggedUserId,
    isComercialFilterRestricted,
    loadingViewValues,
    loadingComercialUsers,
    loadingConsultantUsers,
    comerciaisOptions,
    consultoresOptions,
    brokerId,
    selectedBroker,
    setCorretor,
    comercialUserId,
    setComercialUserId,
    consultantUserId,
    setConsultantUserId,
    periodo,
    setPeriodo,
    periodoInvalido,
    setPeriodoInvalido,
    displayedComercialUserId,
    validatePeriodo,
    resetFiltrosBase,
  } = useContatosPortalFiltros()

  const [metricas, setMetricas] = useState<RelatorioDashboardMetricas | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [source, setSource] = useState<'rpc' | 'fallback' | null>(null)

  const carregar = useCallback(
    async (override?: {
      periodo?: RelatorioPeriodo
      brokerId?: string | null
      comercialUserId?: string
      consultantUserId?: string
    }) => {
      const periodoAtual = override?.periodo ?? periodo
      const brokerIdAtual = override?.brokerId !== undefined ? override.brokerId : brokerId
      const comercialIdAtual =
        override?.comercialUserId !== undefined
          ? override.comercialUserId
          : isComercialFilterRestricted
            ? loggedUserId || '\0'
            : comercialUserId
      const comercialFiltro =
        comercialIdAtual && comercialIdAtual !== '\0' ? comercialIdAtual : null
      const consultantFiltro =
        override?.consultantUserId !== undefined ? override.consultantUserId : consultantUserId

      setLoading(true)
      setError(null)

      const periodoErro = validatePeriodo(periodoAtual)
      if (periodoErro) {
        setPeriodoInvalido(true)
        setError(periodoErro)
        setMetricas(null)
        setLoading(false)
        return
      }
      setPeriodoInvalido(false)

      const resultado =
        await comercialContatosPortalDashboardService.fetchRelatorioContatosDashboard({
          data_inicio: periodoAtual.dataInicio,
          data_fim: periodoAtual.dataFim,
          comercial_user_id: comercialFiltro,
          consultant_user_id: consultantFiltro || null,
          broker_id: brokerIdAtual,
        })

      if (resultado.error && !resultado.data) {
        setError(resultado.error)
        setMetricas(null)
      } else {
        setMetricas(resultado.data)
        setSource(resultado.source ?? null)
      }

      setLoading(false)
    },
    [
      brokerId,
      comercialUserId,
      consultantUserId,
      isComercialFilterRestricted,
      loggedUserId,
      periodo,
      setPeriodoInvalido,
      validatePeriodo,
    ]
  )

  const initialLoadDone = useRef(false)

  useEffect(() => {
    if (loadingViewValues) return
    if (isComercialFilterRestricted && !loggedUserId) return
    if (initialLoadDone.current) return
    initialLoadDone.current = true
    void carregar({
      comercialUserId: isComercialFilterRestricted ? loggedUserId || '\0' : '',
    })
  }, [carregar, loadingViewValues, isComercialFilterRestricted, loggedUserId])

  const handleBuscar = () => {
    void carregar()
  }

  const handleLimparFiltros = () => {
    const reset = resetFiltrosBase()
    setError(null)
    void carregar({
      periodo: reset.periodo,
      brokerId: reset.brokerId,
      comercialUserId: reset.comercialUserId,
      consultantUserId: reset.consultantUserId,
    })
  }

  const dadosPorDia = useMemo(() => {
    if (!metricas) return []
    return comercialContatosPortalDashboardService.fillPorDiaSeries(
      metricas.por_dia,
      metricas.data_inicio,
      metricas.data_fim
    )
  }, [metricas])

  const periodoLabel = useMemo(() => {
    if (!metricas?.data_inicio || !metricas?.data_fim) return 'Últimos 30 dias'
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split('-')
      return `${d}/${m}/${y}`
    }
    return `${fmt(metricas.data_inicio)} a ${fmt(metricas.data_fim)}`
  }, [metricas])

  const topCorretoresItems = useMemo<RankedItem[]>(() => {
    if (!metricas) return []
    return metricas.top_corretoras.map(c => ({
      key: c.broker_id,
      title: c.nome,
      subtitle: `${c.quantidade.toLocaleString('pt-BR')} contato${c.quantidade === 1 ? '' : 's'}`,
    }))
  }, [metricas])

  const topMotivosItems = useMemo<RankedItem[]>(() => {
    if (!metricas) return []
    return metricas.top_motivos.map(m => ({
      key: `${m.motivo_id}-${m.nome}`,
      title: m.nome,
      subtitle: `${m.quantidade.toLocaleString('pt-BR')} registro${m.quantidade === 1 ? '' : 's'}`,
    }))
  }, [metricas])

  const dashboardContent = (() => {
    if (loading) {
      return (
        <div className="flex-1 flex items-center justify-center py-16">
          <LoadingSpinner size="lg" className="mx-auto" />
        </div>
      )
    }

    if (error && !metricas) {
      return null
    }

    if (!metricas) {
      return (
        <div className="flex-1 flex items-center justify-center py-16">
          <Text color="muted">Nenhum dado disponível para o período.</Text>
        </div>
      )
    }

    return (
      <div className="px-6 pb-6 space-y-4">
        <div>
          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
            Visão geral
          </Heading>
          <Text size="sm" color="muted">
            {periodoLabel}
            {source === 'fallback' && ' - agregação local (RPC não disponível)'}
          </Text>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Contatos por dia
              </Heading>
            </div>
            <Text size="sm" color="muted">
              Evolução diária no período
            </Text>
          </div>

          {dadosPorDia.length === 0 ? (
            <Text color="muted">Sem contatos no período.</Text>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dadosPorDia}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-gray-300 dark:stroke-gray-600" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  interval="preserveStartEnd"
                />
                <YAxis tick={{ fill: 'currentColor', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="quantidade"
                  name="Contatos"
                  stroke={PRIMARY_CHART}
                  strokeWidth={2}
                  dot={{ fill: PRIMARY_CHART, r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Meio de contato
                </Heading>
              </div>
              <Text size="sm" color="muted">
                Distribuição por canal
              </Text>
            </div>

            {metricas.por_meio.length === 0 ? (
              <Text color="muted">Sem dados de meio de contato.</Text>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={metricas.por_meio}
                    dataKey="quantidade"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    label={pieLabelWithColor(
                      i =>
                        MEIO_COLORS[metricas.por_meio[i]?.meio ?? ''] ??
                        CHART_PALETTE[i % CHART_PALETTE.length]
                    )}
                  >
                    {metricas.por_meio.map((entry, index) => (
                      <Cell
                        key={entry.meio}
                        fill={MEIO_COLORS[entry.meio] ?? PIE_FALLBACK_COLORS[index % PIE_FALLBACK_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <PhoneIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Tipo de contato
                </Heading>
              </div>
              <Text size="sm" color="muted">
                Ativo vs receptivo
              </Text>
            </div>

            {metricas.por_tipo.length === 0 ? (
              <Text color="muted">Sem dados de tipo de contato.</Text>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={metricas.por_tipo}
                    dataKey="quantidade"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    label={pieLabelWithColor(
                      i =>
                        TIPO_COLORS[metricas.por_tipo[i]?.tipo ?? ''] ??
                        CHART_PALETTE[i % CHART_PALETTE.length]
                    )}
                  >
                    {metricas.por_tipo.map((entry, index) => (
                      <Cell
                        key={entry.tipo}
                        fill={TIPO_COLORS[entry.tipo] ?? PIE_FALLBACK_COLORS[index % PIE_FALLBACK_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <TrophyIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Top 10 - Corretores
                </Heading>
              </div>
              <Text size="sm" color="muted">
                Mais contatadas no período
              </Text>
            </div>

            <RankedList
              items={topCorretoresItems}
              emptyMessage="Nenhum corretor com contatos no período."
            />
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <TagIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Top 10 - Motivos de contato
                </Heading>
              </div>
              <Text size="sm" color="muted">
                Motivos mais frequentes no período
              </Text>
            </div>

            <RankedList items={topMotivosItems} emptyMessage="Nenhum motivo registrado no período." />
          </div>
        </div>
      </div>
    )
  })()

  return (
    <div className="flex flex-col">
      {!loading && metricas && (
        <div className="px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-4 gap-y-2">
            <GradientKpiCard
              label="Total de contatos"
              value={metricas.total_contatos.toLocaleString('pt-BR')}
              gradientClass="from-primary-500 to-primary-600"
            />
            <GradientKpiCard
              label="Entradas no diário"
              value={metricas.total_diario.toLocaleString('pt-BR')}
              gradientClass="from-yellow-500 to-yellow-600"
              subtitleClass="text-yellow-100"
            />
            <GradientKpiCard
              label="Corretores contatados"
              value={metricas.corretoras_distintas.toLocaleString('pt-BR')}
              gradientClass="from-purple-500 to-purple-600"
              subtitleClass="text-purple-100"
            />
            <GradientKpiCard
              label="Contatos ativos"
              value={`${metricas.pct_ativo.toLocaleString('pt-BR')}%`}
              gradientClass="from-green-500 to-green-600"
              subtitleClass="text-green-100"
            />
          </div>
        </div>
      )}

      <ContatosPortalFiltrosSection onBuscar={handleBuscar} onLimpar={handleLimparFiltros} loading={loading}>
        <FiltroField label="Comercial responsável">
          <ContatosPortalResponsavelFiltroInput
            value={displayedComercialUserId}
            onChange={setComercialUserId}
            usuarios={comerciaisOptions}
            disabled={loadingComercialUsers || isComercialFilterRestricted}
            restricted={isComercialFilterRestricted}
          />
        </FiltroField>
        <FiltroField label="Consultor responsável">
          <ContatosPortalResponsavelFiltroInput
            value={consultantUserId}
            onChange={setConsultantUserId}
            usuarios={consultoresOptions}
            disabled={loadingConsultantUsers}
          />
        </FiltroField>
        <FiltroField label="Corretor">
          <ContatosPortalCorretorFiltroInput
            value={brokerId}
            initialBroker={selectedBroker}
            onChange={setCorretor}
          />
        </FiltroField>
        <ContatosPortalPeriodoFiltroInput
          layout="grid"
          value={periodo}
          onChange={next => {
            setPeriodo(next)
            setPeriodoInvalido(false)
          }}
          invalid={periodoInvalido}
        />
      </ContatosPortalFiltrosSection>

      {error && (
        <div className="px-6 pb-2">
          <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/20 dark:text-red-200 text-sm">
            {error}
          </div>
        </div>
      )}

      {dashboardContent}
    </div>
  )
}

export default ContatosPortalDashboardTab
